import React from "react";
import {
  DataGrid,
  GridOverlay,
  GridPagination,
  GridToolbarProps,
} from "@mui/x-data-grid";
import ToolbarWithAdd from "./ToolbarWithAdd";
import CustomPaginationActions from "./CustomPaginationActions";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import DataGridProps from "../interfaces/DataGridProps";
import SkeletonLoader from "./SkeletonLoader";
import MobileAccordionTable from "./MobileAccordionTable";

const DataGrids: React.FC<DataGridProps> = ({
  rows,
  columns,
  components,
  style,
  toolbar = true,
  pageSizeOptions,
  paginationModel,
  rowCount,
  onPaginationModelChange,
  onSearchQueryChange,
  searchQuery,
  onToolbarAddClick,
  toolbarAddLabel,
  toolbarShowAddIcon,
  paginationMode,
  loading,
  getRowId,
  width = "100%",
  height = "70vh",
  sx,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const screenHeight = window.innerHeight;
  const screenWidth = window.innerWidth;

  // Adjust height based on screen size
  if (screenHeight > 1500) height = "auto";
  else if (screenHeight > 1000) height = "auto";
  else if (screenHeight === 642 && screenWidth === 768) height = "auto";
  else if (screenHeight < 800 && screenHeight > 650) height = "auto";

  /** Custom empty overlay */
  function CustomNoRowsOverlay() {
    return (
      <GridOverlay>
        <Typography
          variant={isMobile ? "body2" : "h6"}
          component="p"
          gutterBottom
          sx={{ mt: 1, fontSize: "0.875rem" }}
        >
          {rows.length === 0 && searchQuery ? "No match found" : "No rows"}
        </Typography>
      </GridOverlay>
    );
  }

if (isMobile) {
  const safePagination = paginationModel ?? { page: 0, pageSize: 10 };
  const safeRowCount = rowCount ?? 0;

  return (
       <Box sx={{ width: "100%", mt: 1, position: "relative", ...(sx || {}) }}>
      {toolbar && (
        <ToolbarWithAdd
          searchQuery={searchQuery || ""}
          onSearchQueryChange={onSearchQueryChange || (() => {})}
          onAddClick={onToolbarAddClick}
          addLabel={toolbarAddLabel}
          showAddIcon={toolbarShowAddIcon}
        />
      )}

      <MobileAccordionTable
        rows={rows}
        columns={columns}
        page={safePagination.page}
        pageSize={safePagination.pageSize}
        rowCount={safeRowCount}
        loading={loading}
        onView={props.onMobileView}      
        onDelete={props.onMobileDelete}
        onReport={props.onMobileReport}
        onEnrolled={props.onMobileEnrolled}
        onStatusToggle={props.onMobileStatusToggle}
        onPageChange={(nextPage) => {
          onPaginationModelChange?.({
            page: nextPage,
            pageSize: safePagination.pageSize,
          });
        }}
      />

      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
        >
          <SkeletonLoader />
        </Box>
      )}
    </Box>
  );
}


  // ✅ DESKTOP + TABLET VIEW (default DataGrid)
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        height: {
          xs: toolbar ? "calc(100vh - 180px)" : "calc(100vh - 120px)",
          sm: toolbar ? "calc(65vh - 220px)" : "calc(65vh - 160px)",
          md: toolbar ? "calc(90vh - 220px)" : "calc(90vh - 160px)",
        },
        minHeight: "250px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        ...(sx || {}),
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        pagination
        getRowSpacing={(params) => ({
          top: params.isFirstVisible ? 0 : 4,
          bottom: params.isLastVisible ? 0 : 4,
        })}
        components={{
          Toolbar: (props: GridToolbarProps) =>
            toolbar ? (
              <ToolbarWithAdd
                searchQuery={searchQuery || ""}
                onSearchQueryChange={onSearchQueryChange || (() => {})}
                onAddClick={onToolbarAddClick}
                addLabel={toolbarAddLabel}
                showAddIcon={toolbarShowAddIcon}
              />
            ) : null,
          NoRowsOverlay: CustomNoRowsOverlay,
          Pagination: GridPagination,
          ...components,
        }}
        componentsProps={{
          pagination: { ActionsComponent: CustomPaginationActions } as any,
        }}
        paginationModel={paginationModel}
        rowCount={rowCount}
        paginationMode={paginationMode || "server"}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={pageSizeOptions || [5, 10, 20, 30]}
        style={style}
        loading={loading}
        getRowClassName={(params: any) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
        }
        disableRowSelectionOnClick
        getRowId={getRowId}
        {...props}
        sx={{
          backgroundColor: "#FFFFFF",
          border: "none",
          minWidth: 0,
          fontFamily: "Poppins, Roboto, sans-serif",
          "& .MuiDataGrid-main": {
            minWidth: 0,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "#FFFFFF",
            paddingBottom: 0,
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          },
          "& .MuiDataGrid-virtualScrollerContent": {
            minWidth: "max-content",
          },
          "& .MuiDataGrid-row": {
            backgroundColor: "#F9F9FB",
            // borderRadius: "12px",
            border: "1px solid #F0F0F0",
          },
          "& .MuiDataGrid-row.even-row": { backgroundColor: "#F9F9FB" },
          "& .MuiDataGrid-row.odd-row": { backgroundColor: "#F9F9FB" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#F9F9FB",
            borderBottom: "1px solid #ECECEC",
            minHeight: 52,
            minWidth: "max-content",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            fontSize: "14px",
            color: "#2B2B2B",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            fontSize: "13px",
            color: "#1F2937",
            px: 1, // reduced horizontal padding
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: "#FFFFFF",
            borderTop: "1px solid #ECECEC",
            minHeight: 56,
            marginTop: 0,
          },
          "& .MuiTablePagination-root": {
            alignItems: "center",
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: 13,
                color: "#6B7280",
              },
            "& .MuiTablePagination-displayedRows": {
              marginRight: "16px",
            },
            "& .MuiTablePagination-actions": {
              display: "flex",
              alignItems: "center",
              gap: "12px",
            },
          },
        }}
        filterMode="server"
        // Client-side sorting: the grid re-orders the loaded rows immediately on
        // header click. (Server mode was set without an onSortModelChange handler
        // or any sort param on the queries, so column sorting did nothing.)
        sortingMode="client"
      />

      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            transform: {
              xs: "translateY(40px)",
              sm: "translateY(48px)",
              md: "translateY(56px)",
            },
          }}
        >
          <SkeletonLoader />
        </Box>
      )}
    </Box>
  );
};

export default DataGrids;
